import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { chooseEmail, confirmApp, myTwoFactor, removeApp, renewRecoveryCodes, setupApp } from '../../api/twoFactor';
import type { AppSetup, MyTwoFactor } from '../../api/twoFactor';
import { useSession } from '../../session/useSession';
import { AuthShell } from '../auth/AuthShell';
import { Alert, problemMessage } from '../form';
import { formatDate } from '../format';
import { Icon } from '../Icon';
import { CodeInput } from './CodeInput';

type Stage = { readonly kind: 'idle' } | { readonly kind: 'setup'; readonly setup: AppSetup; readonly qr: string } | { readonly kind: 'codes'; readonly codes: readonly string[] } | { readonly kind: 'confirm'; readonly action: 'remove' | 'renew' };

/**
 * La double authentification du compte connecté (planche 31, D39) : son état,
 * l'activation d'une application (code QR, clé, premier code), les codes de
 * secours montrés UNE fois, le retrait et le renouvellement (qui demandent un
 * code juste), et le code par courriel volontaire.
 *
 * `enroll` : la version de la page forcée, quand la politique exige
 * l'application et que le compte n'en a pas. `onEnrolled` est appelé quand les
 * codes de secours ont été vus.
 */
export function TwoFactorCard({ enroll = false, onEnrolled }: { readonly enroll?: boolean; onEnrolled?(): void }): ReactNode {
    const { t, locale } = useSession();
    const [state, setState] = useState<MyTwoFactor | null>(null);
    const [stage, setStage] = useState<Stage>({ kind: 'idle' });
    const [code, setCode] = useState('');
    const [pending, setPending] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const [flash, setFlash] = useState<string | null>(null);

    useEffect(() => {
        myTwoFactor()
            .then(setState)
            .catch((cause: unknown) => setFailure(problemMessage(t, cause)));
    }, [t]);

    async function run(action: () => Promise<void>): Promise<void> {
        setPending(true);
        setFailure(null);
        setFlash(null);

        try {
            await action();
        } catch (cause) {
            setFailure(problemMessage(t, cause));
            setCode('');
        } finally {
            setPending(false);
        }
    }

    const start = (): Promise<void> =>
        run(async () => {
            const setup = await setupApp();
            // Chargé à la demande : la bibliothèque ne sert qu'ici, une fois par compte.
            const { toDataURL } = await import('qrcode');
            const qr = await toDataURL(setup.uri, { margin: 1, width: 192, errorCorrectionLevel: 'M' });
            setCode('');
            setStage({ kind: 'setup', setup, qr });
        });

    const confirm = (value: string): Promise<void> =>
        run(async () => {
            const result = await confirmApp(value);
            setState(result.state);
            setStage({ kind: 'codes', codes: result.recoveryCodes });
        });

    const confirmAction = (action: 'remove' | 'renew', value: string): Promise<void> =>
        run(async () => {
            if (action === 'remove') {
                setState(await removeApp(value));
                setStage({ kind: 'idle' });
                setFlash(t('twoFactor.profile.removed'));
            } else {
                const result = await renewRecoveryCodes(value);
                setState(result.state);
                setStage({ kind: 'codes', codes: result.recoveryCodes });
            }
        });

    if (state === null) {
        return failure === null ? <p className="muted">{t('common.loading')}</p> : <Alert tone="error">{failure}</Alert>;
    }

    const summary = state.app
        ? t('twoFactor.profile.app', { date: formatDate(state.appSince, locale) ?? '' })
        : state.required
          ? t(state.mode === 'app' ? 'twoFactor.profile.mustEnroll' : 'twoFactor.profile.email')
          : t('twoFactor.profile.off');

    return (
        <div className="two-factor-card">
            <div className="two-factor-card__status">
                <span className={`badge badge--${state.required ? 'ok' : 'muted'}`}>{state.required ? t('twoFactor.profile.on') : t('twoFactor.profile.offShort')}</span>
                <span className="muted">{summary}</span>
            </div>
            <p className="muted two-factor-card__policy">{t(`twoFactor.policy.${state.mode}`)}</p>

            {flash === null ? null : <Alert tone="success">{flash}</Alert>}
            {failure === null ? null : <Alert tone="error">{failure}</Alert>}

            {stage.kind === 'setup' ? (
                <ol className="two-factor-setup">
                    <li>
                        <span className="two-factor-setup__title">{t('twoFactor.setup.install')}</span>
                        <span className="muted">{t('twoFactor.setup.installHint')}</span>
                    </li>
                    <li>
                        <span className="two-factor-setup__title">{t('twoFactor.setup.scan')}</span>
                        <div className="two-factor-setup__qr">
                            <img src={stage.qr} width={176} height={176} alt={t('twoFactor.setup.qrAlt')} />
                            <div className="two-factor-setup__key">
                                <span className="muted">{t('twoFactor.setup.key')}</span>
                                <code>{stage.setup.secret}</code>
                                <button className="button button--ghost button--with-icon" type="button" onClick={() => void navigator.clipboard?.writeText(stage.setup.secret.replace(/\s/g, ''))}>
                                    <Icon name="copy" size={16} />
                                    {t('twoFactor.setup.copy')}
                                </button>
                            </div>
                        </div>
                    </li>
                    <li>
                        <span className="two-factor-setup__title">{t('twoFactor.setup.code')}</span>
                        <CodeInput value={code} onChange={setCode} label={t('twoFactor.code')} disabled={pending} onComplete={(full) => void confirm(full)} />
                        <div className="two-factor-card__actions">
                            {enroll ? null : (
                                <button className="button button--ghost" type="button" onClick={() => setStage({ kind: 'idle' })}>
                                    {t('common.cancel')}
                                </button>
                            )}
                            <button className="button" type="button" disabled={pending || code.length !== 6} onClick={() => void confirm(code)}>
                                {t('twoFactor.setup.activate')}
                            </button>
                        </div>
                    </li>
                </ol>
            ) : stage.kind === 'codes' ? (
                <RecoveryCodesPanel
                    codes={stage.codes}
                    onDone={() => {
                        setStage({ kind: 'idle' });
                        onEnrolled?.();
                    }}
                />
            ) : stage.kind === 'confirm' ? (
                <div className="two-factor-confirm">
                    <span>{t(stage.action === 'remove' ? 'twoFactor.profile.confirmRemove' : 'twoFactor.profile.confirmRenew')}</span>
                    <CodeInput value={code} onChange={setCode} label={t('twoFactor.code')} disabled={pending} autoFocus onComplete={(full) => void confirmAction(stage.action, full)} />
                    <div className="two-factor-card__actions">
                        <button className="button button--ghost" type="button" onClick={() => setStage({ kind: 'idle' })}>
                            {t('common.cancel')}
                        </button>
                        <button className={`button${stage.action === 'remove' ? ' button--danger' : ''}`} type="button" disabled={pending || code.length !== 6} onClick={() => void confirmAction(stage.action, code)}>
                            {t(stage.action === 'remove' ? 'twoFactor.profile.remove' : 'twoFactor.profile.renew')}
                        </button>
                    </div>
                </div>
            ) : state.app ? (
                <>
                    <p className="two-factor-card__codes">
                        <Icon name="key" size={16} />
                        {t('twoFactor.profile.codesLeft', { count: state.recoveryCodesLeft })}
                    </p>
                    <div className="two-factor-card__actions">
                        <button className="button button--ghost" type="button" onClick={() => { setCode(''); setStage({ kind: 'confirm', action: 'renew' }); }}>
                            {t('twoFactor.profile.renew')}
                        </button>
                        {state.mode === 'app' ? null : (
                            <button className="button button--danger-soft" type="button" onClick={() => { setCode(''); setStage({ kind: 'confirm', action: 'remove' }); }}>
                                {t('twoFactor.profile.remove')}
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <div className="two-factor-card__actions two-factor-card__actions--start">
                    <button className="button button--with-icon" type="button" disabled={pending} onClick={() => void start()}>
                        <Icon name="phone" size={18} />
                        {t('twoFactor.profile.enable')}
                    </button>
                </div>
            )}

            {!enroll && state.mode === 'none' && stage.kind === 'idle' ? (
                <label className="two-factor-card__email">
                    <span>
                        <span className="two-factor-card__email-title">{t('twoFactor.profile.emailToggle')}</span>
                        <span className="muted">{t('twoFactor.profile.emailToggleHint')}</span>
                    </span>
                    <input className="switch" type="checkbox" role="switch" checked={state.email} disabled={pending} onChange={(event) => { const wanted = event.target.checked; void run(async () => setState(await chooseEmail(wanted))); }} />
                </label>
            ) : null}
        </div>
    );
}

/** Les huit codes de secours, montrés UNE fois : à télécharger ou copier. */
function RecoveryCodesPanel({ codes, onDone }: { readonly codes: readonly string[]; onDone(): void }): ReactNode {
    const { t } = useSession();
    const text = codes.join('\n');

    function download(): void {
        const url = URL.createObjectURL(new Blob([`${t('twoFactor.codes.fileHeader')}\n\n${text}\n`], { type: 'text/plain;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'kelassi-codes-de-secours.txt';
        document.body.append(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    return (
        <div className="recovery-codes">
            <span className="two-factor-setup__title">{t('twoFactor.codes.title')}</span>
            <span className="muted">{t('twoFactor.codes.body')}</span>
            <ul className="recovery-codes__list">
                {codes.map((code) => (
                    <li key={code}>
                        <code>{code}</code>
                    </li>
                ))}
            </ul>
            <div className="two-factor-card__actions two-factor-card__actions--start">
                <button className="button button--ghost button--with-icon" type="button" onClick={download}>
                    <Icon name="download" size={16} />
                    {t('twoFactor.codes.download')}
                </button>
                <button className="button button--ghost button--with-icon" type="button" onClick={() => void navigator.clipboard?.writeText(text)}>
                    <Icon name="copy" size={16} />
                    {t('twoFactor.codes.copy')}
                </button>
            </div>
            <div className="note note--warn">
                <Icon name="alert" size={18} />
                <span>{t('twoFactor.codes.warning')}</span>
            </div>
            <div className="two-factor-card__actions">
                <button className="button" type="button" onClick={onDone}>
                    {t('twoFactor.codes.done')}
                </button>
            </div>
        </div>
    );
}

/** D39 — la politique exige l'application : rien d'autre avant de l'avoir activée. */
export function ForcedTwoFactorPage(): ReactNode {
    const { t, signOut, refreshMe } = useSession();

    return (
        <AuthShell>
            <div className="auth-form auth-form--wide">
                <div className="auth-form__head">
                    <h1>{t('twoFactor.forced.title')}</h1>
                    <span className="muted">{t('twoFactor.forced.body')}</span>
                </div>
                <TwoFactorCard enroll onEnrolled={() => void refreshMe()} />
                <button className="link-button auth-form__back" type="button" onClick={() => void signOut()}>
                    {t('session.signOut')}
                </button>
            </div>
        </AuthShell>
    );
}
